import { Add, Save } from '@mui/icons-material'
import { Button, Stack } from '@mui/material'
import { GridFilterModel, GridFilterPanel, useGridApiContext } from '@mui/x-data-grid-premium'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsThemeV2 } from '@omnea-app/omui/NewThemeProvider'
import { FilterModel, validateFilterModel } from '@omnea-app/packages/universal/saved-filters/validateFilterModel'
import { useUpdateSavedFilter } from '../hooks/useUpdateSavedFilter'
import { CreateSavedFilterDialog } from './CreateSavedFilterDialog'
import { SavedFilter, useSavedFilterContext } from './SavedFilterContext'

export const ControlledTableFilterPanel = ({ hideSaveButton = false }: { hideSaveButton?: boolean }) => {
  const { enqueueSnackbar } = useSnackbar()
  const gridApi = useGridApiContext()
  const { t } = useTranslation('requests')

  const [isCreatingSavedFilter, setIsCreatingSavedFilter] = React.useState(false)

  const { savedFilter, isLoadingSavedFilter } = useSavedFilterContext()

  const hasActiveSavedFilter = !!savedFilter

  const [validFilterModel, setValidFilterModel] = useState<FilterModel | null>(null)
  const filterModelIsValid = (filterModel: GridFilterModel): filterModel is FilterModel => {
    const errorMessageKey = validateFilterModel(gridApi.current?.state.filter.filterModel)
    if (!errorMessageKey) {
      setValidFilterModel(gridApi.current?.state.filter.filterModel as FilterModel)
      return true
    }

    setValidFilterModel(null)
    enqueueSnackbar(t(`grid-toolbar-filter-groups.invalid-filter-model-error.${errorMessageKey}`), { variant: 'error' })
    return false
  }

  const handleStartCreatingSavedFilter = () => {
    const filterModel = gridApi.current?.state.filter.filterModel
    if (!filterModel || !filterModelIsValid(filterModel)) return

    setValidFilterModel(filterModel)
    setIsCreatingSavedFilter(true)
  }

  const updateSavedFilter = useUpdateSavedFilter()
  const handleUpdateSavedFilterModel = async (filterToUpdate: SavedFilter) => {
    const filterModel = gridApi.current?.state.filter.filterModel
    if (!filterModel || !filterModelIsValid(filterModel)) return

    const isSuccessful = await updateSavedFilter(filterToUpdate, { filters: filterModel })

    // updateSavedFilter will show an error/success snackbar
    if (isSuccessful) {
      gridApi.current?.hideFilterPanel()
    }
  }
  const isThemeV2 = useIsThemeV2()

  if (isLoadingSavedFilter) {
    return null
  }

  return (
    <Stack sx={{ p: 2, position: 'relative' }}>
      <GridFilterPanel
        sx={
          isThemeV2
            ? {
                '& .MuiDataGrid-panelFooter': {
                  justifyContent: 'left',
                },
                '& .MuiDataGrid-panelFooter .MuiButtonBase-root:last-child': {
                  display: hasActiveSavedFilter ? 'none' : 'flex',
                },
              }
            : {
                '& .MuiDataGrid-filterForm': {
                  gap: 1,
                },
                '& .MuiDataGrid-filterForm .MuiInputBase-inputSizeSmall': {
                  padding: '8px 2px',
                },
                '& .MuiAutocomplete-input': {
                  padding: '8px 2px !important',
                  margin: 0,
                },
                '& .MuiDataGrid-panelFooter': {
                  justifyContent: 'left',
                  mt: 4,
                  px: 2,
                },
                '& .MuiDataGrid-panelFooter .MuiButtonBase-root:first-of-type': {
                  color: 'primary.main',
                },
                '& .MuiDataGrid-panelFooter .MuiButtonBase-root:last-child': {
                  color: 'neutralLight.900',
                  ml: 2,
                  display: hasActiveSavedFilter ? 'none' : 'flex',
                },
              }
        }
        filterFormProps={{
          logicOperatorInputProps: {
            color: 'primary',
          },
          columnInputProps: {
            color: 'primary',
          },
          operatorInputProps: {
            color: 'primary',
          },
          valueInputProps: {
            InputComponentProps: {
              color: 'primary',
            },
          },
        }}
      />

      {/* Creating new saved filter */}
      {!hasActiveSavedFilter && (
        <>
          {!hideSaveButton && (
            <Button
              onClick={handleStartCreatingSavedFilter}
              sx={{ width: 'fit-content', position: 'absolute', right: 12, bottom: 12 }}
              startIcon={<Add />}
              color={isThemeV2 ? 'primary' : 'secondary'}
              variant="outlined"
            >
              {t('grid-toolbar-filter-groups.create-filter-group')}
            </Button>
          )}
          {validFilterModel && (
            <CreateSavedFilterDialog
              isOpen={isCreatingSavedFilter}
              onClose={() => setIsCreatingSavedFilter(false)}
              filterModel={validFilterModel}
            />
          )}
        </>
      )}

      {/* Editing saved filter */}
      {hasActiveSavedFilter && (
        <Stack sx={{ flexDirection: 'row', gap: 2, position: 'absolute', right: 12, bottom: 12 }}>
          <Button
            onClick={() => gridApi.current?.setFilterModel(savedFilter.filters)}
            sx={{ width: 'fit-content' }}
            color="neutral"
          >
            {t('grid-toolbar-filter-groups.cancel-modifying')}
          </Button>
          <Button
            onClick={() => handleUpdateSavedFilterModel(savedFilter)}
            sx={{ width: 'fit-content' }}
            startIcon={<Save />}
            color="secondary"
            variant="outlined"
          >
            {t('grid-toolbar-filter-groups.save-filter-group')}
          </Button>
        </Stack>
      )}
    </Stack>
  )
}
